/* eslint-disable simple-import-sort/imports */
import { Navigate, useRoutes } from 'react-router-dom'

import AuthLayout from '@/layouts/auth'
import DashboardLayout from '@/layouts/default'
import { ProtectedRoute, BillingGuard, RouteGuard } from '@qse/config'
import { AuthRoutes } from '@qse/constants'
import {
  LoginScreen,
  RegisterScreen,
  CompleteRegistrationPage,
  CompleteResellerRegistrationPage,
  ConfirmEmailPage,
  NewPasswordScreen,
  ForgotPasswordScreen,
  ResetPasswordScreen,
  TwoFAVerifyScreen,
  VerificationScreen,
  Setup2FAScreen,
  ProfileScreen,
  CheckInvitation,
  CheckResellerInvitation,
} from '@qse/auth-ui'
import {
  ResellersManagement,
  TenantManagement,
  UsersManagement,
  PlanManagement,
  StorageReport,
  TenantAnalytics,
} from '@/pages/management'
import { AppRoutesList } from './routes'
import Billing from '@/pages/billing/billing'
import Success from '@/pages/billing/success'
import Error from '@/pages/billing/error'
import { UserRoleEnum } from '@qse/constants'

/** Super-Admin portal — SUPER_ADMIN and RESELLER_ADMIN (same image, trimmed nav). */
function AppRoutes() {
  const routes = useRoutes([
    {
      path: '/',
      element: <Navigate replace to={AppRoutesList.login} />,
    },
    {
      element: (
        <ProtectedRoute type="public">
          <AuthLayout />
        </ProtectedRoute>
      ),
      children: [
        { path: AppRoutesList.login, element: <LoginScreen /> },
        { path: AppRoutesList.register, element: <RegisterScreen /> },
        { path: AppRoutesList.completeRegistration, element: <CompleteRegistrationPage /> },
        { path: AppRoutesList.confirmEmail, element: <ConfirmEmailPage /> },
        { path: AppRoutesList.newPassword, element: <NewPasswordScreen /> },
        { path: AppRoutesList.forgotPassword, element: <ForgotPasswordScreen /> },
        { path: AppRoutesList.resetPassword, element: <ResetPasswordScreen /> },
        { path: AppRoutesList.verification, element: <VerificationScreen /> },
        { path: AppRoutesList.twoFA, element: <Setup2FAScreen /> },
        { path: AppRoutesList.twoFAVerify, element: <TwoFAVerifyScreen /> },
      ],
    },
    {
      path: AppRoutesList.success,
      element: (
        <ProtectedRoute type="protected">
          <BillingGuard>
            <Success />
          </BillingGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: AppRoutesList.error,
      element: <Error />,
    },
    {
      path: AppRoutesList.checkInvitation,
      element: <CheckInvitation />,
    },
    {
      path: AppRoutesList.checkResellerInvitation,
      element: <CheckResellerInvitation />,
    },
    {
      path: AuthRoutes.completeResellerRegistration,
      element: (
        <ProtectedRoute type="public">
          <AuthLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <CompleteResellerRegistrationPage />,
        },
      ],
    },
    {
      element: (
        <ProtectedRoute type="protected">
          <BillingGuard>
            <DashboardLayout />
          </BillingGuard>
        </ProtectedRoute>
      ),
      children: [
        {
          path: AppRoutesList.billing,
          element: <Billing />,
        },
        {
          path: AppRoutesList.profile,
          element: <ProfileScreen />,
        },
        {
          path: AppRoutesList.reseller,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN]}>
              <ResellersManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.tenants,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <TenantManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.tenant,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <TenantManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.tenantUsers,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <UsersManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.plan,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <PlanManagement />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.storageReport,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <StorageReport />
            </RouteGuard>
          ),
        },
        {
          path: AppRoutesList.tenantAnalytics,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.SUPER_ADMIN, UserRoleEnum.RESELLER_ADMIN]}>
              <TenantAnalytics />
            </RouteGuard>
          ),
        },
        {
          path: '*',
          element: (
            <Navigate
              replace
              state={{ showNotFoundError: true }}
              to={AppRoutesList.tenants}
            />
          ),
        },
      ],
    },
  ])

  return routes
}

export default AppRoutes
